import React, { useMemo, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../utils/toast';
import { CheckCircle2, Loader2, AlertCircle, FileText, ImageIcon, Film, Radio, Scissors, RotateCcw, X, Expand, Wand2, FileSearch } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MontageTimelinePanel from './MontageTimelinePanel';
import NavigableLightbox from './NavigableLightbox';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_META = [
  { id: 'script',     label: 'Script',    icon: FileText,   hint: 'AI writes 5 shots' },
  { id: 'keyframes',  label: 'Keyframes', icon: ImageIcon,  hint: 'GPT Image 2 × 5 frames' },
  { id: 'videos',     label: 'Videos',    icon: Film,       hint: 'Seedance i2v × 5 clips' },
  { id: 'timeline',   label: 'Timeline',  icon: Scissors,   hint: 'Trim · review · export' },
];

const STAGE_ORDER = ['script', 'keyframes', 'videos', 'timeline', 'done'];

function stageState(stage, currentStage, runStatus, stageData) {
  // If this specific stage has complete data (e.g. all 5 keyframes OK),
  // show it as 'done' even if a later stage failed. Only cascade 'failed'
  // onto stages that don't have their output ready yet.
  const hasCompleteData = stageData?.complete;
  if (runStatus === 'failed' && !hasCompleteData) return 'failed';
  if (currentStage === 'done') return 'done';
  if (hasCompleteData) return 'done';
  const curIdx = STAGE_ORDER.indexOf(currentStage || 'script');
  const myIdx = STAGE_ORDER.indexOf(stage);
  if (myIdx < curIdx) return 'done';
  if (myIdx === curIdx) return 'running';
  return 'pending';
}

export default function TVCPipelineView({ tvcState, runStatus, runId, token }) {
  const s = tvcState || {};
  const currentStage = s.current_stage || 'script';
  const script = s.script || [];
  const keyframes = s.keyframes || [];
  const videos = s.videos || [];
  const cinematography = s.cinematography || null;
  const scriptV3 = s.script_v3 || null;
  const breakdown = s.breakdown || null;   // M2 output: strategic 5-shot breakdown
  const motionStage = s.stages?.stage_motion_v3 || null;  // M4 per-shot motion audit
  const draftId = s.timeline_draft_id;
  const [lightbox, setLightbox] = useState(null); // { items: [{url,type,label}], index: number }
  const [regenBusy, setRegenBusy] = useState({});   // shot_idx → 'kf' | 'clip'
  const [iterateOpen, setIterateOpen] = useState(null); // { shot_idx, current_url }
  const [auditOpen, setAuditOpen] = useState(null); // { shot_idx }
  const [scriptOpen, setScriptOpen] = useState(false);
  const [seenIterateErrors, setSeenIterateErrors] = useState({}); // shot_idx → timestamp

  // Surface backend iterate failures as toasts (bg task can fail silently
  // otherwise — user would just see the original image back with no feedback).
  useEffect(() => {
    keyframes.forEach((kf) => {
      if (kf?.iterate_error && kf?.iterate_failed_at && seenIterateErrors[kf.shot_idx] !== kf.iterate_failed_at) {
        toast.error(`Keyframe ${kf.shot_idx + 1} iteration failed: ${kf.iterate_error}`);
        setSeenIterateErrors((prev) => ({ ...prev, [kf.shot_idx]: kf.iterate_failed_at }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyframes]);

  // Build the enlarge-gallery for a given kind + clicked shot index
  const openLightbox = useCallback((kind, shotIdx) => {
    const items = [];
    if (kind === 'kf') {
      for (const kf of keyframes) {
        if (kf.url) items.push({ url: kf.url, type: 'image', label: `Keyframe ${kf.shot_idx + 1}`, shot_idx: kf.shot_idx });
      }
    } else {
      for (const v of videos) {
        const url = v.clip_url || v.placeholder_url;
        if (!url) continue;
        items.push({ url, type: v.status === 'ok' ? 'video' : 'image', label: `Clip ${v.shot_idx + 1}`, shot_idx: v.shot_idx });
      }
    }
    if (!items.length) return;
    const idx = Math.max(0, items.findIndex((x) => x.shot_idx === shotIdx));
    setLightbox({ items, index: idx });
  }, [keyframes, videos]);

  const stageStates = useMemo(() => {
    // A stage is "complete" if its output data is fully populated.
    // Keyframes stage: all 5 KFs have status ok or failed (terminal).
    // Videos stage: all 5 clips have terminal status.
    // Script stage: script array has 5 shots.
    // Timeline stage: timeline_draft_id is set.
    const kfComplete = keyframes.length === 5 && keyframes.every(k => k.status === 'ok' || k.status === 'failed');
    const kfAllOk = keyframes.length === 5 && keyframes.every(k => k.status === 'ok');
    const videoComplete = videos.length === 5 && videos.every(v => v.status === 'ok' || v.status === 'placeholder' || v.status === 'failed');
    const scriptComplete = script.length >= 5;
    const timelineComplete = !!draftId;
    const data = {
      script: { complete: scriptComplete },
      keyframes: { complete: kfAllOk },  // only mark 'done' when all 5 actually succeeded
      videos: { complete: videoComplete },
      timeline: { complete: timelineComplete },
    };
    const out = {};
    for (const m of STAGE_META) out[m.id] = stageState(m.id, currentStage, runStatus, data[m.id]);
    return out;
  }, [currentStage, runStatus, keyframes, videos, script, draftId]);

  const fireRegen = useCallback(async (kind, shotIdx) => {
    if (!runId) return toast.error('Run not ready yet');
    if (regenBusy[shotIdx]) return;
    setRegenBusy(prev => ({ ...prev, [shotIdx]: kind }));
    try {
      const path = kind === 'kf'
        ? `/api/recipes/tvc-20s/${runId}/regenerate-keyframe/${shotIdx}`
        : `/api/recipes/tvc-20s/${runId}/retry-shot/${shotIdx}`;
      await axios.post(`${API}${path}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(kind === 'kf' ? `Regenerating keyframe ${shotIdx + 1}…` : `Retrying clip ${shotIdx + 1}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      // Let polling clear the busy flag naturally on next state push
      setTimeout(() => setRegenBusy(prev => { const x = { ...prev }; delete x[shotIdx]; return x; }), 2000);
    }
  }, [runId, token, regenBusy]);

  const fireIterate = useCallback(async (shotIdx, instruction) => {
    if (!runId) return toast.error('Run not ready yet');
    try {
      await axios.post(
        `${API}/api/recipes/tvc-20s/${runId}/iterate-keyframe/${shotIdx}`,
        { instruction },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Iterating keyframe ${shotIdx + 1}…`);
      setIterateOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Iterate failed');
    }
  }, [runId, token]);

  return (
    <div className="flex flex-col gap-4 px-4 pt-2 pb-6 w-full" data-testid="tvc-pipeline-view">
      {s.brief && (
        <div className="rounded-xl px-4 py-3" style={{ background: 'rgba(0,240,255,0.04)', border: '1px solid rgba(0,240,255,0.15)' }}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400/60 mb-1">Brief</div>
          <div className="text-xs text-slate-200 leading-relaxed">{s.brief}</div>
        </div>
      )}

      {/* Stage 1 — Script */}
      <StageCard meta={STAGE_META[0]} state={stageStates.script} testid="tvc-stage-script">
        {scriptV3 && (scriptV3.display_script || scriptV3.title) && (
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setScriptOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider text-cyan-300 hover:text-cyan-200 transition"
              style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.25)' }}
              data-testid="tvc-view-script-btn"
            >
              <FileText className="w-3 h-3" />
              View customer-friendly script
            </button>
            {scriptV3.title && (
              <span className="text-[11px] text-white/70 italic">"{scriptV3.title}"</span>
            )}
            {scriptV3.tonality && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded text-amber-300/80" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                {scriptV3.tonality}
              </span>
            )}
          </div>
        )}
        {script.length > 0 && (
          <div className="grid gap-2">
            {script.map((shot) => (
              <div key={shot.shot_idx} className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid={`tvc-script-shot-${shot.shot_idx}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded text-cyan-300/80" style={{ background: 'rgba(0,240,255,0.08)' }}>SH{shot.shot_idx + 1}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{shot.slug}</span>
                </div>
                <div className="text-xs text-slate-300 leading-snug">{shot.description}</div>
                {shot.dialogue && (
                  <div className="mt-1 text-[11px] italic text-cyan-300/70 flex items-start gap-1">
                    <Radio className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                    <span>"{shot.dialogue}"</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </StageCard>

      {/* Stage 2 — Keyframes */}
      <StageCard meta={STAGE_META[1]} state={stageStates.keyframes} testid="tvc-stage-keyframes">
        {keyframes.length > 0 && (
          <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory -mx-1 px-1 pb-1 sm:grid sm:grid-cols-5 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0 sm:snap-none"
               style={{ scrollbarWidth: 'thin' }}>
            {keyframes.map((kf) => (
              <div
                key={`kf-wrap-${kf.shot_idx}`}
                className="w-[45vw] max-w-[220px] shrink-0 snap-start sm:w-auto sm:max-w-none sm:shrink"
              >
                <MediaTile
                  key={`kf-${kf.shot_idx}`}
                  label={`KF ${kf.shot_idx + 1}`}
                  status={kf.status}
                  mediaUrl={kf.url}
                  mediaType="image"
                  error={kf.error}
                  onEnlarge={() => openLightbox('kf', kf.shot_idx)}
                  onRegenerate={() => fireRegen('kf', kf.shot_idx)}
                  onIterate={() => setIterateOpen({ shot_idx: kf.shot_idx, url: kf.url })}
                  onAudit={kf.logs ? () => setAuditOpen({ shot_idx: kf.shot_idx }) : null}
                  regenBusy={regenBusy[kf.shot_idx] === 'kf'}
                  testid={`tvc-keyframe-${kf.shot_idx}`}
                />
              </div>
            ))}
          </div>
        )}
      </StageCard>

      {/* Stage 3 — Videos */}
      <StageCard meta={STAGE_META[2]} state={stageStates.videos} testid="tvc-stage-videos">
        {videos.length > 0 && (
          <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory -mx-1 px-1 pb-1 sm:grid sm:grid-cols-5 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0 sm:snap-none"
               style={{ scrollbarWidth: 'thin' }}>
            {videos.map((v) => {
              const kf = keyframes.find((k) => k.shot_idx === v.shot_idx) || {};
              const kfIterated = (kf.version && kf.version > 1) || !!kf.last_instruction;
              return (
                <div
                  key={`v-wrap-${v.shot_idx}`}
                  className="w-[45vw] max-w-[220px] shrink-0 snap-start sm:w-auto sm:max-w-none sm:shrink"
                >
                  <MediaTile
                    key={`v-${v.shot_idx}`}
                    label={`Clip ${v.shot_idx + 1}`}
                    status={v.status}
                    mediaUrl={v.clip_url || v.placeholder_url}
                    mediaType={v.status === 'ok' ? 'video' : 'image'}
                    error={v.error}
                    onEnlarge={() => openLightbox('clip', v.shot_idx)}
                    onRegenerate={() => fireRegen('clip', v.shot_idx)}
                    regenBusy={regenBusy[v.shot_idx] === 'clip'}
                    kfOutOfSync={kfIterated && v.status === 'ok'}
                    testid={`tvc-clip-${v.shot_idx}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </StageCard>

      {/* Stage 4 — Timeline (embedded Montage editor, scoped to recipe_slug='tvc-20s') */}
      <StageCard meta={STAGE_META[3]} state={stageStates.timeline} testid="tvc-stage-timeline">
        {draftId ? (
          <div className="-mx-2 -mb-2" data-testid="tvc-timeline-embed">
            <MontageTimelinePanel token={token} runId={runId} recipeSlug="tvc-20s" />
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 px-2 py-4 text-center">
            Waiting for clips to finish — the timeline editor will mount here as soon as the draft is ready.
          </div>
        )}
      </StageCard>

      {/* Lightbox */}
      {lightbox && (
        <NavigableLightbox
          items={lightbox.items}
          index={lightbox.index}
          onIndexChange={(i) => setLightbox((prev) => prev ? { ...prev, index: i } : null)}
          onClose={() => setLightbox(null)}
          testid="tvc-lightbox"
        />
      )}

      {/* Iterate Keyframe prompt modal */}
      {iterateOpen && (
        <IteratePrompt
          shotIdx={iterateOpen.shot_idx}
          currentUrl={iterateOpen.url}
          onSubmit={(instruction) => fireIterate(iterateOpen.shot_idx, instruction)}
          onClose={() => setIterateOpen(null)}
        />
      )}

      {/* Prompt Audit drawer */}
      {auditOpen && (() => {
        const kf = keyframes.find(k => k.shot_idx === auditOpen.shot_idx);
        const enriched = (cinematography?.shots_enriched || []).find(e => e.shot_idx === auditOpen.shot_idx);
        const breakdownItem = (breakdown?.breakdown || []).find(b => b.shot_idx === auditOpen.shot_idx);
        const motionAudit = (motionStage?.per_shot || []).find(m => m.shot_idx === auditOpen.shot_idx);
        return (
          <PromptAuditDrawer
            keyframe={kf}
            enriched={enriched}
            continuityBlock={cinematography?.continuity_block}
            scriptShot={(script || [])[auditOpen.shot_idx]}
            breakdownItem={breakdownItem}
            motionAudit={motionAudit}
            onClose={() => setAuditOpen(null)}
          />
        );
      })()}

      {/* Customer-friendly script drawer (V3 Stage 1 output) */}
      {scriptOpen && scriptV3 && (
        <ScriptDrawer scriptV3={scriptV3} onClose={() => setScriptOpen(false)} />
      )}
    </div>
  );
}

/* ─── Stage shell ─── */
function StageCard({ meta, state, testid, children }) {
  const Icon = meta.icon;
  const stateConfig = {
    pending:  { borderColor: 'rgba(255,255,255,0.06)', iconColor: '#64748B',  tagText: 'PENDING', tagBg: 'rgba(255,255,255,0.04)', tagColor: '#64748B' },
    running:  { borderColor: 'rgba(0,240,255,0.3)',    iconColor: '#00F0FF',  tagText: 'RUNNING', tagBg: 'rgba(0,240,255,0.1)',    tagColor: '#00F0FF' },
    done:     { borderColor: 'rgba(34,197,94,0.35)',   iconColor: '#22C55E',  tagText: 'DONE',    tagBg: 'rgba(34,197,94,0.1)',    tagColor: '#22C55E' },
    failed:   { borderColor: 'rgba(239,68,68,0.4)',    iconColor: '#EF4444',  tagText: 'FAILED',  tagBg: 'rgba(239,68,68,0.1)',    tagColor: '#EF4444' },
  }[state] || {};

  return (
    <div className="rounded-2xl p-4" style={{ background: 'rgba(10,21,32,0.4)', border: `1px solid ${stateConfig.borderColor}`, transition: 'border-color 0.3s' }} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stateConfig.iconColor}14`, border: `1px solid ${stateConfig.iconColor}33` }}>
            {state === 'running' ? (
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: stateConfig.iconColor }} />
            ) : state === 'done' ? (
              <CheckCircle2 className="w-4 h-4" style={{ color: stateConfig.iconColor }} />
            ) : state === 'failed' ? (
              <AlertCircle className="w-4 h-4" style={{ color: stateConfig.iconColor }} />
            ) : (
              <Icon className="w-4 h-4" style={{ color: stateConfig.iconColor }} />
            )}
          </div>
          <div>
            <div className="text-sm font-bold text-white tracking-tight">{meta.label}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{meta.hint}</div>
          </div>
        </div>
        <span className="text-[9px] font-mono px-2 py-0.5 rounded tracking-wider" style={{ background: stateConfig.tagBg, color: stateConfig.tagColor }}>
          {stateConfig.tagText}
        </span>
      </div>
      {children}
    </div>
  );
}

/* ─── Single tile (keyframe or clip) with enlarge + regenerate icons ─── */
function MediaTile({ label, status, mediaUrl, mediaType, error, onEnlarge, onRegenerate, onIterate, onAudit, regenBusy, kfOutOfSync, testid }) {
  const isPending = status === 'pending' || !status;
  const isOk = status === 'ok';
  const isPlaceholder = status === 'placeholder';
  const isFailed = status === 'failed';
  const canEnlarge = !!mediaUrl && !isPending;
  // Regenerate available on placeholder OR failed tiles (keyframe + clip both),
  // AND on OK clips whose upstream keyframe was iterated (needs resync).
  const canRegen = isFailed || isPlaceholder || kfOutOfSync;
  // Iterate available on ANY ready keyframe (onIterate only passed for KFs)
  const canIterate = !!onIterate && isOk && !isPending;
  // Audit available when backend persisted a `logs` block for this shot
  const canAudit = !!onAudit;

  return (
    <div
      className="relative aspect-video rounded-lg overflow-hidden group cursor-pointer"
      style={{ background: '#050A10', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={testid}
      onClick={() => canEnlarge && onEnlarge(mediaUrl)}
    >
      {mediaUrl && (
        mediaType === 'video' ? (
          <video src={mediaUrl} className="w-full h-full object-cover" muted loop autoPlay playsInline />
        ) : (
          <img src={mediaUrl} alt={label} className="w-full h-full object-cover" loading="lazy" />
        )
      )}

      {/* Pending shimmer */}
      {isPending && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-cyan-400/60" />
        </div>
      )}

      {/* Hover overlay: enlarge icon */}
      {canEnlarge && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="rounded-full p-2" style={{ background: 'rgba(0,0,0,0.6)' }}>
            <Expand className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Placeholder / failed badge */}
      {(isPlaceholder || isFailed) && !regenBusy && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-1 text-center pointer-events-none" style={{ background: isPlaceholder ? 'rgba(0,0,0,0.35)' : 'rgba(239,68,68,0.18)' }}>
          {isPlaceholder ? (
            <RotateCcw className="w-3 h-3 text-amber-300" title={error || ''} />
          ) : (
            <AlertCircle className="w-3 h-3 text-red-300" title={error || ''} />
          )}
          <span className="text-[8px] font-mono uppercase tracking-wider text-white/70">
            {isPlaceholder ? 'Placeholder' : 'Failed'}
          </span>
        </div>
      )}

      {/* Regenerate pill */}
      {canRegen && (
        <button
          onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
          disabled={regenBusy}
          className={`absolute top-1.5 right-1.5 flex items-center gap-1 px-1.5 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider transition-all disabled:opacity-60 ${kfOutOfSync && !regenBusy ? 'animate-pulse' : ''}`}
          style={kfOutOfSync ? {
            background: 'rgba(245,158,11,0.22)', color: '#FCD34D',
            border: '1px solid rgba(245,158,11,0.5)', backdropFilter: 'blur(4px)',
          } : {
            background: 'rgba(0,240,255,0.18)', color: '#00F0FF',
            border: '1px solid rgba(0,240,255,0.35)', backdropFilter: 'blur(4px)',
          }}
          data-testid={`${testid}-regen`}
          title={kfOutOfSync ? 'Keyframe was iterated — rerun Seedance to sync this clip' : 'Regenerate this clip'}
        >
          {regenBusy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5" />}
          {regenBusy ? 'Running' : (kfOutOfSync ? 'Sync' : 'Regen')}
        </button>
      )}

      {/* Iterate pill — KFs only, always visible on ready frames */}
      {canIterate && !canRegen && (
        <button
          onClick={(e) => { e.stopPropagation(); onIterate(); }}
          className="absolute top-1.5 right-1.5 flex items-center gap-1 px-1.5 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider transition-all opacity-0 group-hover:opacity-100"
          style={{ background: 'rgba(168,85,247,0.22)', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.45)', backdropFilter: 'blur(4px)' }}
          data-testid={`${testid}-iterate`}
          title="Iterate this keyframe with a text instruction"
        >
          <Wand2 className="w-2.5 h-2.5" />
          Iterate
        </button>
      )}

      {/* Audit pill — bottom-left, only when logs are present */}
      {canAudit && (
        <button
          onClick={(e) => { e.stopPropagation(); onAudit(); }}
          className="absolute top-1.5 left-1.5 p-1 rounded-md transition-all opacity-0 group-hover:opacity-100"
          style={{ background: 'rgba(30,58,138,0.55)', color: '#DBEAFE', border: '1px solid rgba(96,165,250,0.5)', backdropFilter: 'blur(4px)' }}
          data-testid={`${testid}-audit`}
          title="Prompt audit — see what GPT sent, what gpt-image-2 revised it to, model, tool params, latency, usage"
        >
          <FileSearch className="w-3 h-3" />
        </button>
      )}

      {/* Label */}
      <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.75), transparent)' }}>
        <span className="text-[9px] font-mono tracking-wider text-white/90">{label}</span>
        {isOk && <CheckCircle2 className="inline-block ml-1 w-2.5 h-2.5 text-emerald-400" />}
      </div>
    </div>
  );
}

/* ─── Iterate keyframe prompt modal ─── */
function IteratePrompt({ shotIdx, currentUrl, onSubmit, onClose }) {
  const [instruction, setInstruction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!instruction.trim() || submitting) return;
    setSubmitting(true);
    try { await onSubmit(instruction.trim()); } finally { setSubmitting(false); }
  };
  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="tvc-iterate-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl p-5"
        style={{ background: '#0A1520', border: '1px solid rgba(168,85,247,0.3)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)' }}>
            <Wand2 className="w-4 h-4 text-purple-300" />
          </div>
          <div>
            <div className="text-sm font-bold text-white tracking-tight">Iterate Keyframe {shotIdx + 1}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">GPT-Image-2 · targeted edit</div>
          </div>
        </div>
        {currentUrl && (
          <img src={currentUrl} alt="current" className="w-full aspect-video object-cover rounded-lg mb-3" style={{ border: '1px solid rgba(255,255,255,0.06)' }} />
        )}
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder='e.g. "Change the lighting to golden-hour, warmer tones" or "Add a subtle lens flare in the top-right"'
          rows={3}
          className="w-full px-3 py-2 rounded-lg text-xs text-white placeholder:text-slate-600 outline-none resize-none focus:ring-1"
          style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
          data-testid="tvc-iterate-input"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-400 hover:text-white transition"
            data-testid="tvc-iterate-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!instruction.trim() || submitting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white transition disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #A855F7, #7E22CE)' }}
            data-testid="tvc-iterate-submit"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            Iterate
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─── Prompt Audit Drawer ─── */
/* Shows per-shot logs persisted by _stage_keyframes_v2 — input_prompt, revised_prompt,
   model, engine, response_id, tool_params, input_image_refs, usage, latency. */
function PromptAuditDrawer({ keyframe, enriched, continuityBlock, scriptShot, breakdownItem, motionAudit, onClose }) {
  const logs = (keyframe || {}).logs || {};
  const shotIdx = keyframe?.shot_idx ?? 0;

  const copy = (txt) => {
    try {
      navigator.clipboard.writeText(txt || '');
      toast.success('Copied');
    } catch (_e) {
      toast.error('Copy failed');
    }
  };

  const Section = ({ title, children, copyText }) => (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300/80">{title}</div>
        {copyText && (
          <button
            onClick={() => copy(copyText)}
            className="text-[9px] font-mono uppercase tracking-wider text-white/50 hover:text-cyan-300 transition"
          >
            copy
          </button>
        )}
      </div>
      <div className="rounded-lg p-3 text-xs text-white/85 font-mono leading-relaxed whitespace-pre-wrap break-words"
           style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(255,255,255,0.06)', maxHeight: '260px', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );

  const keyVal = (k, v) => (
    <div className="flex gap-2 text-xs">
      <span className="text-white/45 font-mono min-w-[120px]">{k}</span>
      <span className="text-white/85 font-mono break-all">{v ?? '—'}</span>
    </div>
  );

  const hasLogs = Object.keys(logs).length > 0;

  return (
    <div
      className="fixed inset-0 z-[10000] flex"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="tvc-audit-drawer"
    >
      <div className="flex-1" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full overflow-y-auto"
        style={{ background: '#0A1520', borderLeft: '1px solid rgba(0,240,255,0.2)' }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b" style={{ background: '#0A1520', borderColor: 'rgba(255,255,255,0.08)' }}>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Prompt Audit — Shot {shotIdx + 1}</div>
            <div className="text-[10px] font-mono text-white/40 mt-0.5">{scriptShot?.slug || ''}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
            data-testid="tvc-audit-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          {/* Thumbnail + script context */}
          {keyframe?.url && (
            <div className="mb-4 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <img src={keyframe.url} alt={`Shot ${shotIdx + 1}`} className="w-full aspect-video object-cover" />
            </div>
          )}

          {scriptShot && (
            <Section title="Director script (thin)" copyText={scriptShot.visual_prompt}>
              <div className="text-white/70 italic mb-1.5">{scriptShot.description}</div>
              <div>{scriptShot.visual_prompt}</div>
            </Section>
          )}

          {breakdownItem && (
            <Section title="M2 · Strategic breakdown (director's contract)"
              copyText={JSON.stringify(breakdownItem, null, 2)}>
              <div className="space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 mb-0.5">Objective</div>
                  <div className="text-white/85">{breakdownItem.shot_objective || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 mb-0.5">Emotional purpose (viewer feels)</div>
                  <div className="text-white/85">{breakdownItem.emotional_purpose || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 mb-0.5">Product role</div>
                  <div className="text-white/85">{breakdownItem.product_purpose || '—'}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300/70">Product visibility</div>
                  <span
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{
                      background:
                        breakdownItem.product_visibility === 'closeup' ? 'rgba(34,197,94,0.15)' :
                        breakdownItem.product_visibility === 'hero'    ? 'rgba(6,182,212,0.15)'  :
                        breakdownItem.product_visibility === 'background' ? 'rgba(245,158,11,0.12)' :
                                                                            'rgba(239,68,68,0.12)',
                      color:
                        breakdownItem.product_visibility === 'closeup' ? '#22C55E' :
                        breakdownItem.product_visibility === 'hero'    ? '#06B6D4' :
                        breakdownItem.product_visibility === 'background' ? '#F59E0B' :
                                                                            '#EF4444',
                    }}
                    data-testid={`audit-product-visibility-${breakdownItem.shot_idx}`}
                  >
                    {breakdownItem.product_visibility || 'none'}
                  </span>
                </div>
                {breakdownItem.key_visual_beats && breakdownItem.key_visual_beats.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 mb-0.5">Key visual beats</div>
                    <ul className="list-disc list-inside space-y-0.5 text-white/85">
                      {breakdownItem.key_visual_beats.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </Section>
          )}

          {enriched?.cinematic_prompt && (
            <Section title="Cinematographer enriched prompt" copyText={enriched.cinematic_prompt}>
              {enriched.cinematic_prompt}
            </Section>
          )}

          {motionAudit?.motion_prompt && (
            <Section title={`M4 · Intentional motion prompt (${motionAudit.fallback_used ? 'FALLBACK' : 'AI'})`}
              copyText={motionAudit.motion_prompt}>
              <div>{motionAudit.motion_prompt}</div>
              {motionAudit.error && !motionAudit.fallback_used && (
                <div className="mt-2 text-[10px] text-amber-300/80">note: {motionAudit.error}</div>
              )}
            </Section>
          )}

          {!hasLogs && (
            <div className="rounded-lg p-3 text-xs text-amber-300/90 font-mono"
                 style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}>
              No logs captured for this shot. This usually means the run used the legacy batched/parallel keyframe path,
              not the v2 pipeline. Re-run with cinematography enabled to see full audit data.
            </div>
          )}

          {hasLogs && (
            <>
              <Section title={`Input prompt sent to ${logs.engine || 'image model'}`} copyText={logs.input_prompt}>
                {logs.input_prompt || '(empty)'}
              </Section>

              {logs.revised_prompt && (
                <Section title="gpt-image-2 revised_prompt (what the image model actually saw)" copyText={logs.revised_prompt}>
                  {logs.revised_prompt}
                </Section>
              )}

              <Section title="Metadata">
                <div className="space-y-1">
                  {keyVal('engine', logs.engine)}
                  {keyVal('model', logs.model)}
                  {keyVal('tool_params', logs.tool_params ? JSON.stringify(logs.tool_params) : null)}
                  {keyVal('response_id', logs.response_id)}
                  {keyVal('previous_response_id', logs.previous_response_id)}
                  {keyVal('latency_ms', logs.latency_ms)}
                  {keyVal('attempts', logs.attempts)}
                  {keyVal('timestamp', logs.timestamp)}
                  {keyVal('usage', logs.usage ? JSON.stringify(logs.usage) : null)}
                </div>
              </Section>

              {logs.input_image_refs && logs.input_image_refs.length > 0 && (
                <Section title="Input image references (anchor chaining)">
                  <pre className="text-[11px]">{JSON.stringify(logs.input_image_refs, null, 2)}</pre>
                </Section>
              )}
            </>
          )}

          {continuityBlock && (
            <Section title="Locked continuity_block (all shots)" copyText={JSON.stringify(continuityBlock, null, 2)}>
              <pre className="text-[11px]">{JSON.stringify(continuityBlock, null, 2)}</pre>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Script Drawer (V3 Stage 1 customer-friendly script) ─── */
/* Renders the prose `display_script` from recipe_runs.tvc_state.script_v3.
   Click CTA in Script stage card → slide-out drawer. */
function ScriptDrawer({ scriptV3, onClose }) {
  const copy = (txt) => {
    try {
      navigator.clipboard.writeText(txt || '');
      toast.success('Copied');
    } catch (_e) {
      toast.error('Copy failed');
    }
  };

  const display = scriptV3?.display_script || '';
  const title = scriptV3?.title || 'Ad Concept';
  const coreThought = scriptV3?.core_thought || '';
  const tonality = scriptV3?.tonality || '';
  const vo = scriptV3?.vo_option || '';
  const chars = scriptV3?.characters || [];
  const spine = scriptV3?.story_spine || {};

  return (
    <div
      className="fixed inset-0 z-[10000] flex"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="tvc-script-drawer"
    >
      <div className="flex-1" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full overflow-y-auto"
        style={{ background: '#0A1520', borderLeft: '1px solid rgba(0,240,255,0.2)' }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b" style={{ background: '#0A1520', borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="min-w-0 pr-3">
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Customer-Friendly Script</div>
            <div className="text-[13px] text-white/85 mt-0.5 truncate">{title}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => copy(display)}
              className="px-2 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-wider text-white/60 hover:text-cyan-300 hover:bg-white/5 transition"
              data-testid="tvc-script-copy"
            >
              Copy
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
              data-testid="tvc-script-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4">
          {(coreThought || tonality) && (
            <div className="mb-4 rounded-lg p-3" style={{ background: 'rgba(0,240,255,0.04)', border: '1px solid rgba(0,240,255,0.15)' }}>
              {coreThought && (
                <div className="mb-1.5">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300/60 mb-0.5">Core thought</div>
                  <div className="text-xs text-white/85 italic">{coreThought}</div>
                </div>
              )}
              {tonality && (
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300/60 mb-0.5">Tonality</div>
                  <div className="text-xs text-white/85">{tonality}</div>
                </div>
              )}
            </div>
          )}

          {/* Rendered markdown script */}
          <div className="rounded-lg p-4 text-sm text-white/90 leading-relaxed"
               style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(255,255,255,0.06)' }}
               data-testid="tvc-script-body">
            <div className="prose prose-invert prose-sm max-w-none prose-headings:text-cyan-300 prose-headings:font-mono prose-headings:uppercase prose-headings:tracking-widest prose-strong:text-cyan-200 prose-em:text-white/70">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{display}</ReactMarkdown>
            </div>
          </div>

          {/* Story spine (machine-safe structure for reference) */}
          {(spine.hook || spine.build || spine.payoff) && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300/80 mb-1.5">Story Spine</div>
              <div className="rounded-lg p-3 text-[11px] font-mono text-white/80 space-y-1.5" style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.05)' }}>
                {spine.hook && <div><span className="text-cyan-300/70">Hook:</span> {spine.hook}</div>}
                {spine.build && <div><span className="text-cyan-300/70">Build:</span> {spine.build}</div>}
                {spine.payoff && <div><span className="text-cyan-300/70">Payoff:</span> {spine.payoff}</div>}
              </div>
            </div>
          )}

          {/* Characters */}
          {chars.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300/80 mb-1.5">Cast</div>
              <div className="space-y-1.5">
                {chars.map((c, i) => (
                  <div key={i} className="rounded-lg p-2.5 text-[11px]" style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono text-cyan-300 text-[10px] uppercase tracking-wider">{c.role}</span>
                      {c.function_in_story && <span className="text-white/50 text-[10px]">· {c.function_in_story}</span>}
                    </div>
                    <div className="text-white/80 leading-snug">{c.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* VO option */}
          {vo && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300/80 mb-1.5">VO</div>
              <div className="rounded-lg p-3 text-xs italic text-amber-300/90" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
                "{vo}"
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

